import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import type { AgenticOutOfScopeFeedback, DislikeFeedbackSubmitPayload } from '../AskAsperAgentic.types';
import DislikeFeedbackPopover from '../../../molecules/AskAsperAccordion/DislikeFeedbackPopover';

export interface OutOfScopeFeedbackCardProps {
    feedback: AgenticOutOfScopeFeedback;
    'data-test'?: string;
    'data-test-id'?: string;
}

const DEFAULT_FEEDBACK_TEXT =
    'Help us with what you needed and we will use it to expand the scope for analysis';

/**
 * OutOfScopeFeedbackCard - Shown in the left panel when a user request is out of scope.
 * Uses the same DislikeFeedbackPopover + submit handler pattern as right-panel artefact feedback.
 */
const OutOfScopeFeedbackCard: React.FC<OutOfScopeFeedbackCardProps> = ({
    feedback,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const [dismissed, setDismissed] = useState(false);
    const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);

    if (dismissed) return null;

    const feedbackText = feedback.description ?? DEFAULT_FEEDBACK_TEXT;

    const handleFeedbackSubmit = (payload: DislikeFeedbackSubmitPayload) => {
        feedback.onDislikeFeedbackSubmit?.(payload);
        setShowFeedbackPopover(false);
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-transparent flex flex-col gap-5 font-['Open_Sans']"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-out-of-scope-feedback-card
            >
                <p className="m-0 text-[16px] leading-[24px] text-[#52525c]">{feedbackText}</p>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg border border-solid border-[#5f49bc] bg-white text-[#5f49bc] text-[14px] leading-[22px] cursor-pointer hover:bg-[#fbfaff] transition-colors"
                            style={{ fontWeight: 600 }}
                            onClick={() => setShowFeedbackPopover(true)}
                            data-test-id={`${dataTestId}-tell-us`}
                        >
                            Tell us what you needed
                        </button>
                        <button
                            type="button"
                            className="px-2 py-2 bg-transparent border-none text-[14px] leading-[22px] text-[#71717a] cursor-pointer hover:text-[#52525c] transition-colors"
                            onClick={() => setDismissed(true)}
                            data-test-id={`${dataTestId}-cancel`}
                        >
                            Cancel
                        </button>
                    </div>

                    {showFeedbackPopover && (
                        <DislikeFeedbackPopover
                            open={showFeedbackPopover}
                            onClose={() => setShowFeedbackPopover(false)}
                            onSubmit={handleFeedbackSubmit}
                            contextTitle={feedback.dislikeFeedbackContextTitle}
                            data-test-id={`${dataTestId}-dislike-feedback`}
                        />
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default OutOfScopeFeedbackCard;
